import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid } from "@mui/material";
import FroalaEditorComponent from "../froalaHtmlEditor/FroalaEditorComponent";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { filterObjectsUptoCategory, timestamp } from "../../utils/services";
import { filterSelectTagIntro } from "./CommentLibraryHelper";
import { addIntroDropdown } from "../../store/action";

const froalaConfig = {
  imagePaste: false,
  spellCheck: true,
  heightMin: 400,
  height: 400,
  heightMax: 590,
  linkAutoPrefix: 'https://',
  linkAlwaysBlank: true,
  toolbarButtons: {
    moreText: { buttons: ['bold', 'italic', 'underline', '|', 'fontFamily', 'fontSize', 'textColor', 'backgroundColor'], align: 'left', buttonsVisible: 7 },
    moreParagraph: { buttons: ['|', 'alignLeft', 'alignCenter', 'alignRight', 'formatOLSimple', 'formatUL'], align: 'left', buttonsVisible: 8 },
    moreRich: { buttons: ['|', "insertLink", "outdent", "indent"], align: "left", buttonsVisible: 1 },
    moreMisc: { buttons: ['undo', 'redo', 'selectAll', 'html', 'clearFormatting'], align: 'right', buttonsVisible: 2 }
  },
  toolbarButtonsXS: [['fontFamily', 'fontSize', 'bold', 'italic', 'underline', '|'], ['undo', 'redo']],
  pluginsEnabled: ["wordPaste", "spellChecker", "codeBeautifier", "fontFamily", "fontSize", "colors", "align", "lists", 'paragraphFormat', "link"],
};

const InlineIntroductoryText = ({ from, isActive, setIsActive, disableDnD, selectedID, accordOpen, AllCategoryList, categoryId, updateTemplate, setAllCategoryList, categoryUuid, filterTagList }) => {
  const isCategoryMatch = (category, id) =>
    category &&
    (category.category_id === id ||
      category.id === id ||
      category.uuid === id ||
      (id !== undefined && id !== null && String(category.category_id) === String(id)) ||
      (id !== undefined && id !== null && String(category.id) === String(id)) ||
      (id !== undefined && id !== null && String(category.uuid) === String(id)));

  const categoryData = AllCategoryList?.template?.categories?.find((category) => isCategoryMatch(category, categoryId));
  const editor = useRef();
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.templates)?.categoryList;
  const storeData = useSelector((state) => state.commentLibrary);
  const [introductory, setIntroductory] = useState({ innerHTML: categoryData?.introductory_text?.replace(/<[^>]+>\s*<\/[^>]+>/g, ""), dropdownList: categoryData?.introductory_text_dropdowns });
  const [introductoryDropdowns, setIntroductoryDropdowns] = useState(categoryData?.introductory_text_dropdowns || []);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (categoryData?.introductory_text_dropdowns?.length && !introductory?.dropdownList) {
      setIntroductory({ innerHTML: categoryData?.introductory_text?.replace(/<[^>]+>\s*<\/[^>]+>/g, ""), dropdownList: categoryData?.introductory_text_dropdowns || [] });
      setIntroductoryDropdowns(categoryData?.introductory_text_dropdowns || []);
    }
  }, [categoryData?.introductory_text_dropdowns]);

  const BorderRadius = (from) => {
    if (from?.split("_")[0] !== "SUBCATEGORY") {
      return "4px";
    }
  };
  const BorderBottomAccord = (from, isOpen) => {
    if (from?.split("_")[0] === "SUBCATEGORY" && isOpen) {
      return "none !important";
    } else if (from?.split("_")[0] === "SUBCATEGORY" && !isOpen) {
      return "1px solid #e7e7e9 ";
    }
  };
  const IsShadowing = (from) => {
    if (from?.split("_")[0] === "CATEGORY" && from?.split("_")[0] === "details") {
      return "-5px 6px 8px 1px #e1e1e1";
    } else if (from?.split("_")[0] === "CATEGORY" || from?.split("_")[0] === "details") {
      return "-5px 6px 8px 1px #e1e1e1";
    } else {
      return "none";
    }
  };

  const handleEditorChange = (name, content) => {
    setIntroductory(prevState => ({ ...prevState, innerHTML: content }));
    setHasChanges(true);
  };

  const handleUpdateTemplateData = (name, value = null, dropdown, type) => {
    const catList = AllCategoryList?.template?.categories;
    const categoryIndex = catList.findIndex((cat) => isCategoryMatch(cat, categoryId));
    const savedContent = filterSelectTagIntro(value || introductory?.innerHTML);
    catList[categoryIndex].introductory_text = savedContent;
    catList[categoryIndex].updated_at = timestamp();
    setIntroductory((prevState) => ({ ...prevState, innerHTML: savedContent }));
    setHasChanges(false);
    if (dropdown) {
      const formData = {
        category_id: categoryId,
        introductory_text: savedContent,
        category_uuid: categoryUuid,
        dropdown_uuid: dropdown?.dropdown_uuid,
        dropdowns_values: dropdown?.dropdowns_values,
        type: type,
        list_guid: dropdown?.list_guid,
      }
      if (dropdown?.selected_value) formData.selected_value = dropdown?.selected_value;
      else formData.selected_value = undefined;
      if (dropdown?.dropdowns_values) formData.dropdowns_values = dropdown?.dropdowns_values;
      else formData.dropdowns_values = undefined;
      dispatch(addIntroDropdown(formData));
    }
    filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
    updateTemplate("noToast");
    setAllCategoryList((prev) => {
      const newCategoryList = { ...AllCategoryList };
      newCategoryList.template.categories = catList;
      newCategoryList.template.categories.updated_at = timestamp();
      return newCategoryList;
    });
  };

  const handleChangeDropdown = (event) => {
    const isDropdownTarget =
      event?.target?.tagName === "SELECT" ||
      event?.target?.classList?.contains("froala-dropdown-input");
    if (!isDropdownTarget) return;

    const catList = AllCategoryList?.template?.categories;
    const categoryIndex = catList.findIndex((cat) => cat.category_id === categoryId);
    const selectedValue = event.target.value?.includes("---") ? null : event.target.value;
    let textDropdowns = catList[categoryIndex].introductory_text_dropdowns;
    let textDropdownIndex = catList[categoryIndex].introductory_text_dropdowns?.findIndex((dropdown) => dropdown.dropdown_uuid === event.target.id);
    if (textDropdownIndex === -1) return;
    textDropdowns[textDropdownIndex] = { ...textDropdowns[textDropdownIndex], selected_value: selectedValue };
    catList[categoryIndex].introductory_text_dropdowns = textDropdowns;
    catList[categoryIndex].updated_at = new Date().toISOString();
    setIntroductoryDropdowns(textDropdowns);
    setHasChanges(false);
    const selectRegex = new RegExp(`<select[^>]*?id=["']${event.target.id}["'][\\s\\S]*?<\\/select>`, "g");
    const editorInstance = editor.current?.editor;
    let editorContent = editorInstance?.html?.get();
    editorContent = editorContent.replace(selectRegex, (selectMatch) => {
      const optionRegex = /<option([^>]*)>([^<]+)<\/option>/g;
      return selectMatch.replace(optionRegex, (optionMatch, attributes, value) => {
        const isSelected = value.trim() === selectedValue.trim();
        const updatedAttributes = attributes.replace(/selected/g, "").trim();
        return `<option ${updatedAttributes}${isSelected ? " selected" : ""}>${value}</option>`;
      });
    });
    editorInstance.html.set(editorContent);
    if (event.target.id) {
      const formData = {
        category_id: categoryId,
        introductory_text: filterSelectTagIntro(editorContent?.innerHTML || introductory?.innerHTML),
        category_uuid: categoryUuid,
        dropdown_uuid: event.target.id,
        type: "edit"
      }
      formData.selected_value = selectedValue;
      dispatch(addIntroDropdown(formData));
    }
    filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
    updateTemplate("noToast");
    setAllCategoryList((prev) => {
      const newCategoryList = { ...AllCategoryList };
      newCategoryList.template.categories = catList;
      newCategoryList.template.categories.updated_at = timestamp();
      return newCategoryList;
    });
  };

  useEffect(() => {
    const editorInstance = editor.current?.editor;
    if (editorInstance) {
      const contentArea = editorInstance.$el.get(0);
      contentArea.addEventListener("change", handleChangeDropdown);
      return () => {
        contentArea.removeEventListener("change", handleChangeDropdown);
      };
    }
  }, [introductory, categoryData]);

  useEffect(() => {
    setAllCategoryList((prev) => {
      const newCategoryList = { ...AllCategoryList };
      newCategoryList.template.categories = AllCategoryList.template.categories;
      newCategoryList.template.categories.updated_at = timestamp();
      return newCategoryList;
    });
    document.addEventListener("changeValue", handleChangeDropdown);
    return () => {
      document.removeEventListener("changeValue", handleChangeDropdown);
    };
  }, [categoryData]);

  return (
    <>
      <div style={{ cursor: "auto", padding: 0 }}  >
        <div
          onClick={(event) => {
            event.stopPropagation();
            setIsActive(!isActive);
            disableDnD && disableDnD(!isActive, selectedID);
          }}
          style={{
            borderRadius: BorderRadius(from),
            boxShadow: IsShadowing(from),
            position: "relative",
            zIndex: isActive && "0",
            borderBottom: BorderBottomAccord(from, accordOpen),
            padding: "10px 0px 10px 10px",
            marginRight: "-10px",
            cursor: "pointer",
            height: "51px",
            alignContent: "center"
          }}
          className="accordion"
        >
          <div
            className="accordion__title"
            style={{
              color: isActive ? "#4282E2" : "#333333",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingRight: "56px"
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "400",
                paddingLeft: "8px",
              }}
            >
              Introductory Text
            </div>
            {isActive ? (
              <ExpandLessIcon sx={{ color: "#4282E2", ml: 4 }} />
            ) : (
              <ExpandMoreIcon sx={{ color: "#333333", ml: 4 }} />
            )}
          </div>
        </div>
        {isActive && (
          <div style={{ padding: "32px", borderRadius: "0 0 8px 8px" }}>
            <Grid container display="flex">
              <FroalaEditorComponent
                categoryId={categoryId}
                editor={editor}
                id={"introTextTE"}
                commentData={categoryData}
                onChange={handleEditorChange}
                editorData={introductory}
                froalaConfig={froalaConfig}
                onBlur={handleUpdateTemplateData}
                setEditorContent={setIntroductory}
                dropdowns={introductoryDropdowns}
                filterTagList={storeData?.clTagList}
              />
            </Grid>
          </div>
        )}
      </div>
    </>
  )
}

export default InlineIntroductoryText;
